// src/components/ChatContent.jsx
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import io from "socket.io-client";
import axios from "axios";
import { useAuthContext, useNotificationContext } from "../context";
import { BASE_URL, conversationsURL } from "../api/api";
import "../App.css";

const SOCKET_URL = BASE_URL.replace("/dating", "");
const socket = io(SOCKET_URL, {
  path: "/dating/socket.io",
  //path: "/socket.io",  //for local testing
  transports: ["websocket"],
  secure: true,
  withCredentials: true,
});
socket.on("connect_error", console.error);

const ChatContent = ({
  senderId,
  recipientId,
  onClose,           
}) => {
  const { user, token, allUsers, fetchAllUsers } = useAuthContext();
  const { fetchChatUnreadCount } = useNotificationContext();

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);

  const messagesRef = useRef(null);
  const hasMarkedReadRef = useRef(false);

  useEffect(() => {
    if (allUsers.length === 0) {
      fetchAllUsers();
    }
  }, [allUsers.length, fetchAllUsers]);

  const recipientUser =
    allUsers.find((u) => u.user_id === recipientId) || {};
  const recipientName = recipientUser.firstName || "Chat Partner";

  // join + new message listeners
  useEffect(() => {
    socket.emit("join conversation", { senderId, recipientId });

    const onJoined = ({ conversationId: cid, conversation }) => {
      setConversationId(cid);
      setMessages(conversation.messages || []);
    };
    const onNewMsg = (msg) => {
      if (!conversationId && msg.conversationId) {
        setConversationId(msg.conversationId);
      }
      setMessages((prev) => [...prev, msg]);
      const c = messagesRef.current;
      if (msg.sender !== user.user_id && c) {
        const { scrollHeight, scrollTop, clientHeight } = c;
        if (scrollHeight - (scrollTop + clientHeight) > 50) {
          setShowNewMessageButton(true);
        }
      }
    };

    socket.on("conversation joined", onJoined);
    socket.on("new message", onNewMsg);
    return () => {
      socket.off("conversation joined", onJoined);
      socket.off("new message", onNewMsg);
    };
  }, [senderId, recipientId, user.user_id, conversationId]);

  // auto‑scroll + mark‑read on mount
  useEffect(() => {
    if (conversationId && !initialScrollDone) {
      const c = messagesRef.current;
      if (c) c.scrollTop = c.scrollHeight;
      setInitialScrollDone(true);
    
     // console.log("[MARK-READ] Initial scroll. Sending mark-read for user:", user.user_id);
    
      axios
        .patch(`${conversationsURL}/${conversationId}/mark-read`, {
          userId: user.user_id,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
      //    console.log("[MARK-READ] Success (initial scroll)");
          fetchChatUnreadCount();
        })
        .catch((err) => {
       //   console.error("[MARK-READ] Failed (initial scroll)", err);
        });
    }
  }, [
    conversationId,
    initialScrollDone,
    user.user_id,
    token,
    fetchChatUnreadCount,
  ]);

  // mark‑read when fits without scroll
  useEffect(() => {
    const c = messagesRef.current;
    if (
      conversationId &&
      c &&
      !hasMarkedReadRef.current
    ) {
      const { scrollHeight, clientHeight } = c;
  
      const hasUnreadFromOthers = messages.some(
        (m) => m.sender !== user.user_id && !m.read
      );
  
      if (scrollHeight <= clientHeight + 50 && hasUnreadFromOthers) {
      //  console.log("[MARK-READ] Messages fit in viewport. Marking as read.");
  
        axios
          .patch(
            `${conversationsURL}/${conversationId}/mark-read`,
            { userId: user.user_id },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .then(() => {
          //  console.log("[MARK-READ] Success (no scroll needed)");
            fetchChatUnreadCount();
            setMessages((prev) =>
              prev.map((m) =>
                m.sender !== user.user_id ? { ...m, read: true } : m
              )
            );
            hasMarkedReadRef.current = true;
          })
          .catch((err) => {
            console.error("[MARK-READ] Failed (no scroll)", err);
          });
      }
    }
  }, [conversationId, messages, user.user_id, token, fetchChatUnreadCount]);  

  // on‑scroll handler
  const handleScroll = () => {
    const c = messagesRef.current;
    if (!c) return;
    const { scrollHeight, scrollTop, clientHeight } = c;
    if (scrollHeight - (scrollTop + clientHeight) < 50) {
      setShowNewMessageButton(false);
      const unread = messages.filter(
        (m) => m.sender !== user.user_id && !m.read
      );
      
     // console.log("[SCROLL] Bottom reached. Unread messages:", unread);
    
      if (unread.length) {
      //  console.log("[MARK-READ] Triggering from scroll with unread count:", unread.length);
    
        axios
          .patch(`${conversationsURL}/${conversationId}/mark-read`, {
            userId: user.user_id,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(() => {
         //   console.log("[MARK-READ] Success (scroll)");
            fetchChatUnreadCount();
            setMessages((prev) =>
              prev.map((m) =>
                m.sender !== user.user_id ? { ...m, read: true } : m
              )
            );
          })
          .catch((err) => {
            console.error("[MARK-READ] Failed (scroll)", err);
          });
      }
    }    
  };

  // send
  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    socket.emit("send message", {
      conversationId,
      senderId: user.user_id,
      recipientId,
      text: input.trim(),
    });
    setInput("");
  };

  // render with dates
  let lastDate = "";
  const renderMessages = () =>
    messages.map((m, i) => {
      const msgDate = new Date(m.createdAt).toLocaleDateString();
      const showDateHeader = msgDate !== lastDate;
      lastDate = msgDate;
      return (
        <React.Fragment key={i}>
          {showDateHeader && (
            <div className="text-center my-4">
              <span className="inline-block bg-gray-300 text-gray-700 text-xs px-3 py-1 rounded-full">
                {msgDate}
              </span>
            </div>
          )}
          <div
            className={`flex ${
              m.sender === user.user_id ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs sm:max-w-sm p-3 rounded-2xl shadow ${
                m.sender === user.user_id
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-900"
              }`}
            >
              <p className="whitespace-pre-line">{m.text}</p>
              <span className="text-xs mt-1 block opacity-70">
                {new Date(m.createdAt).toLocaleTimeString()}
              </span>
            </div>
          </div>
        </React.Fragment>
      );
    });

  return (
    <div className="flex flex-col h-full bg-white p-4 md:p-6 rounded-lg">
      {/* Header */}
      <header className="px-4 py-3 border-b flex items-center justify-between mb-2">
        {/* now calls onClose instead of navigate(-1) */}
        <button onClick={onClose} className="text-blue-500">
          ← Back
        </button>
        <Link
          to={`/user/${recipientUser.user_id}`}
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <img
            src={
              recipientUser.profile_image ||
              "/image/operator.png"
            }
            alt="Profile"
            className="w-10 h-10 rounded-full object-cover"
          />
          <span className="font-semibold text-gray-800">
            {recipientName}
          </span>
        </Link>
      </header>

      {/* Messages */}
      <main
        ref={messagesRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-3 custom-scroll relative"
      >
        {messages.length > 0 ? (
          renderMessages()
        ) : (
          <p className="text-center text-gray-500 mt-10">
            No messages yet
          </p>
        )}

        {showNewMessageButton && (
          <div className="sticky bottom-4 w-full flex justify-center">
            <button
              onClick={() => {
                messagesRef.current.scrollTop =
                  messagesRef.current.scrollHeight;
                setShowNewMessageButton(false);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full shadow"
            >
              ↓ New Messages
            </button>
          </div>
        )}
      </main>

      {/* Input */}
      <form onSubmit={sendMessage} className="px-4 py-3 border-t flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="submit"
          className="bg-blue-500 text-white px-5 py-2 rounded-full"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatContent;
